/**
 * InboxList — full inbox rendering: bulk-action bar, header strip,
 * date-bucket sections, EmailRows, load-more.
 *
 * Pure presentation; all state lives in EmailPage.
 *
 * Iteration 104 — Batch 3 component split.
 */
import React from 'react';
import EmailRow from './EmailRow';

// ─── Date bucket helpers (kept here so InboxList is self-contained) ─────────
export const BUCKET_ORDER = ['Today', 'Yesterday', 'This Week', 'This Month', 'Earlier'];

export function dateBucketOf(dateStr) {
  if (!dateStr) return 'Earlier';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return 'Earlier';
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfYesterday = new Date(startOfToday); startOfYesterday.setDate(startOfYesterday.getDate() - 1);
  const startOfWeek = new Date(startOfToday); startOfWeek.setDate(startOfWeek.getDate() - 7);
  const startOfMonth = new Date(startOfToday); startOfMonth.setDate(startOfMonth.getDate() - 30);
  if (d >= startOfToday) return 'Today';
  if (d >= startOfYesterday) return 'Yesterday';
  if (d >= startOfWeek) return 'This Week';
  if (d >= startOfMonth) return 'This Month';
  return 'Earlier';
}

export function buildThreads(list, threadingEnabled) {
  if (!threadingEnabled) {
    return list.map((e) => ({ key: e.id, latest: e, items: [e], count: 1 }));
  }
  const order = [];
  const map = new Map();
  for (const e of list) {
    const cid = e.conversation_id;
    const key = cid ? `c:${cid}` : `id:${e.id}`;
    if (!map.has(key)) {
      map.set(key, { key, latest: e, items: [e], count: 1 });
      order.push(key);
    } else {
      const t = map.get(key);
      t.items.push(e);
      t.count += 1;
    }
  }
  return order.map((k) => map.get(k));
}

export function groupByBucket(threads) {
  const buckets = {};
  for (const t of threads) {
    const ts = t.latest.received_at || t.latest.sent_at || t.latest.synced_at || t.latest.created_at;
    const b = dateBucketOf(ts);
    if (!buckets[b]) buckets[b] = [];
    buckets[b].push(t);
  }
  return BUCKET_ORDER.filter((b) => buckets[b]).map((name) => ({ name, threads: buckets[name] }));
}

// ─── Main component ──────────────────────────────────────────────────────────
export default function InboxList({
  emails,
  visibleCount,
  setVisibleCount,
  selectedEmail,
  selectedIds,
  toggleSelect,
  selectVisible,
  clearSelection,
  threadingEnabled,
  setThreadingEnabled,
  expandedThreads,
  toggleExpand,
  bulkBusy,
  runBulk,
  folder,
  formatDate,
  handleSelectEmail,
}) {
  const list = Array.isArray(emails) ? emails : [];
  const cap = list.slice(0, visibleCount);
  const threads = buildThreads(cap, threadingEnabled);
  const buckets = groupByBucket(threads);
  const visibleEmailIds = cap.map((e) => e.id);
  const allSelected = visibleEmailIds.length > 0 && visibleEmailIds.every((id) => selectedIds.has(id));
  const someSelected = selectedIds.size > 0;

  return (
    <>
      {someSelected && (
        <div className="sticky top-0 z-10 bg-slate-900 text-white px-3 py-2 flex items-center gap-2 text-sm shadow-sm" data-testid="bulk-action-bar">
          <span className="font-medium" data-testid="bulk-selection-count">{selectedIds.size} selected</span>
          <div className="ml-auto flex items-center gap-1">
            <button onClick={() => runBulk('mark_read')} disabled={bulkBusy} className="px-2.5 py-1 rounded hover:bg-slate-700 text-xs font-medium disabled:opacity-50" data-testid="bulk-mark-read-btn">Mark read</button>
            <button onClick={() => runBulk('mark_unread')} disabled={bulkBusy} className="px-2.5 py-1 rounded hover:bg-slate-700 text-xs font-medium disabled:opacity-50" data-testid="bulk-mark-unread-btn">Mark unread</button>
            <button onClick={() => runBulk('archive')} disabled={bulkBusy} className="px-2.5 py-1 rounded hover:bg-slate-700 text-xs font-medium disabled:opacity-50" data-testid="bulk-archive-btn">Archive</button>
            <button
              onClick={() => { if (window.confirm(`Delete ${selectedIds.size} email${selectedIds.size > 1 ? 's' : ''}? This cannot be undone.`)) runBulk('delete'); }}
              disabled={bulkBusy}
              className="px-2.5 py-1 rounded hover:bg-rose-700 bg-rose-500/30 text-xs font-medium disabled:opacity-50"
              data-testid="bulk-delete-btn"
            >Delete</button>
            <button onClick={clearSelection} className="ml-1 px-2 py-1 rounded hover:bg-slate-700 text-xs" data-testid="bulk-clear-btn" title="Clear selection">✕</button>
          </div>
        </div>
      )}

      {/* Header strip */}
      <div className="flex items-center gap-3 px-3 py-1.5 border-b border-slate-100 dark:border-slate-700 text-xs text-slate-500 dark:text-slate-400 bg-slate-50/50 dark:bg-slate-800/30">
        <label className="inline-flex items-center gap-2 cursor-pointer" title={allSelected ? 'Deselect all visible' : 'Select all visible'}>
          <input
            type="checkbox"
            checked={allSelected}
            onChange={() => allSelected ? clearSelection() : selectVisible(visibleEmailIds)}
            className="h-3.5 w-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            data-testid="select-all-checkbox"
          />
          <span>Select all</span>
        </label>
        <button
          onClick={() => setThreadingEnabled((v) => !v)}
          className={`ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded ${
            threadingEnabled
              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
              : 'hover:bg-slate-100 dark:hover:bg-slate-700'
          }`}
          data-testid="threading-toggle"
          title={threadingEnabled ? 'Threading on — click to flatten' : 'Threading off — click to group'}
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" /></svg>
          Threading {threadingEnabled ? 'on' : 'off'}
        </button>
      </div>

      {/* Date-grouped sections */}
      {buckets.map((bucket) => (
        <div key={bucket.name} data-testid={`date-bucket-${bucket.name.toLowerCase().replace(/\s+/g, '-')}`}>
          <div className="sticky top-[34px] bg-white/95 dark:bg-slate-800/95 backdrop-blur px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 border-b border-slate-100 dark:border-slate-700 z-[5]">
            {bucket.name}
            <span className="ml-1.5 text-slate-400 normal-case font-normal">({bucket.threads.length})</span>
          </div>
          {bucket.threads.map((thread) => (
            <EmailRow
              key={thread.key}
              thread={thread}
              folder={folder}
              isSelected={selectedIds.has(thread.latest.id)}
              isOpen={selectedEmail?.id === thread.latest.id}
              isExpanded={expandedThreads.has(thread.key)}
              formatDate={formatDate}
              onSelect={handleSelectEmail}
              onToggleSelect={toggleSelect}
              onToggleExpand={toggleExpand}
            />
          ))}
        </div>
      ))}

      {Array.isArray(emails) && emails.length > visibleCount && (
        <div className="p-3 text-center border-t border-slate-100 dark:border-slate-700">
          <button
            onClick={() => setVisibleCount(visibleCount + 50)}
            className="text-sm text-blue-600 dark:text-blue-400 hover:underline font-medium"
            data-testid="load-more-emails-btn"
          >
            Load more ({emails.length - visibleCount} remaining)
          </button>
        </div>
      )}
    </>
  );
}
